import { cva } from 'class-variance-authority';
import { ComponentPropsWithoutRef, FC } from 'react';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { cn } from 'shared/utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary/80',
        secondary: 'bg-primary-100 text-primary hover:bg-primary/10',
        outline: 'border border-black/40 bg-transparent text-black/80 hover:bg-black/5',
        success: 'border border-success/40 text-success hover:bg-success/5',
        danger: 'border border-danger/40 text-danger hover:bg-danger/5',
        text: 'text-primary hover:text-primary/80',
        ghost: 'text-black/80 hover:bg-black/5',
      },
      size: { small: 'px-3 py-2 text-xs', medium: 'px-4 py-2.5', large: 'px-4 py-3' },
      shape: { rounded: '', circle: 'rounded-full p-2' },
    },
    defaultVariants: { variant: 'primary', size: 'medium', shape: 'rounded' },
  },
);

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  size?: 'large' | 'medium' | 'small';
  variant?: 'primary' | 'secondary' | 'outline' | 'success' | 'danger' | 'text' | 'ghost';
  shape?: 'rounded' | 'circle';
  loading?: boolean;
}

export const Button: FC<ButtonProps> = ({
  className,
  size = 'medium',
  variant = 'primary',
  shape = 'rounded',
  type = 'button',
  loading = false,
  children,
  ...props
}) => (
  <button
    {...props}
    type={type}
    aria-busy={loading || undefined}
    className={cn(buttonVariants({ variant, size, shape }), className)}
  >
    {loading ? (
      <>
        <LoadingSpinner
          size={size === 'small' ? 14 : 20}
          className="border-current border-t-transparent border-b-current border-l-current"
        />
        <span className="sr-only">{children}</span>
      </>
    ) : (
      children
    )}
  </button>
);
